import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Paper,
  Checkbox,
  ListItemText,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useNavigate } from "react-router-dom";

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import {
  hasPermission,
  PERMISSIONS,
  SUPPORTED_USER_ROLES,
} from "../utils/roleAccess";

import { auth, db } from "../config/firebaseConfig"; // ✅ shared firebase config

// role options list
const roleOptions = SUPPORTED_USER_ROLES;

export default function AuthCard() {
  const [isLogin, setIsLogin] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    role: [], // ✅ multiple roles
    children: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  function handleChange(e) {
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));
  }

  async function handleRegister(e) {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        form.email,
        form.password
      );
      const uid = userCredential.user.uid;

      await setDoc(doc(db, "users", uid), {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        roles: form.role, // ✅ saved as array
        children: form.children
          ? form.children.split(",").map((c) => c.trim())
          : [],
        createdAt: new Date().toISOString(),
      });

      setMessage({
        type: "success",
        text: "User created successfully. You can now login.",
      });
      setIsLogin(true);

      if (
        hasPermission(
          { roles: form.role },
          PERMISSIONS.ADMIN_DASHBOARD,
        )
      ) {
        navigate("/admin-dashboard");
      } else if (form.role.includes("teacher")) navigate("/teacher-dashboard");
      else if (form.role.includes("parent")) navigate(`/student/${uid}`);
      else navigate("/"); // default
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      await signInWithEmailAndPassword(auth, form.email, form.password);
      setMessage({ type: "success", text: "Logged in successfully." });
      // TODO: redirect or update app state
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#fff",
        p: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: 380,
          maxWidth: "95%",
          p: 3,
          borderRadius: 3,
          boxShadow: "0px 10px 30px rgba(0,0,0,0.07)",
        }}
      >
        <Typography variant="h6" align="center" gutterBottom>
          {isLogin ? "Login" : "Create account"}
        </Typography>

        <Box
          component="form"
          onSubmit={isLogin ? handleLogin : handleRegister}
          sx={{ mt: 1 }}
        >
          {!isLogin && (
            <TextField
              fullWidth
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              placeholder="Full Name"
              variant="outlined"
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          )}

          <TextField
            fullWidth
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            variant="outlined"
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            type="email"
          />

          {!isLogin && (
            <TextField
              fullWidth
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Phone Number"
              variant="outlined"
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              type="tel"
            />
          )}

          <TextField
            fullWidth
            name="password"
            value={form.password}
            onChange={handleChange}
            placeholder="Password"
            variant="outlined"
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            type={showPass ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPass((s) => !s)} edge="end">
                    {showPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {!isLogin && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="role-label">Roles</InputLabel>
              <Select
                labelId="role-label"
                multiple
                value={form.role}
                name="role"
                onChange={(e) =>
                  setForm((s) => ({ ...s, role: e.target.value }))
                }
                renderValue={(selected) => selected.join(", ")}
                sx={{ borderRadius: 2 }}
              >
                {roleOptions.map((role) => (
                  <MenuItem key={role} value={role}>
                    <Checkbox checked={form.role.indexOf(role) > -1} />
                    <ListItemText primary={role} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!isLogin && (
            <TextField
              fullWidth
              name="children"
              value={form.children}
              onChange={handleChange}
              placeholder="Children IDs (comma-separated)"
              variant="outlined"
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          )}

          <Button
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              py: 1.6,
              mt: 1,
              borderRadius: 8,
              background: "linear-gradient(180deg,#5b00f2,#6f00ff)",
              color: "#fff",
              boxShadow: "0px 8px 0px rgba(95, 0, 230, 0.2)",
              "&:hover": { opacity: 0.95 },
            }}
          >
            {isLogin ? "Login" : "Create User"}
          </Button>

          <Button
            fullWidth
            onClick={() => setIsLogin((s) => !s)}
            sx={{ mt: 2, textTransform: "none" }}
          >
            {isLogin
              ? "Don't have an account? Create one"
              : "Already have an account? Login"}
          </Button>

          {message && (
            <Typography
              sx={{
                mt: 1,
                color: message.type === "error" ? "error.main" : "success.main",
              }}
            >
              {message.text}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
